/**
 * Smart image fallback system utilities
 * Determines the appropriate fallback image based on product attributes
 */

interface ProductInfo {
  name?: string;
  category?: string;
  sku?: string;
}

// Keywords mapping to specific product types
const PRODUCT_KEYWORDS = {
  cables: [
    'cable', 'hdmi', 'usb', 'ethernet', 'cat5', 'cat6', 'rj45', 
    'coax', 'coaxial', 'fiber', 'optical', 'patch', 'cord', 'wire'
  ],
  setTopBox: [
    'stb', 'set-top', 'set top', 'settop', 'decoder', 'receiver',
    'dvr', 'pvr', 'tuner', 'converter', 'box'
  ],
  remote: [
    'remote', 'control', 'rc', 'clicker', 'controller'
  ],
  accessory: [
    'mount', 'bracket', 'stand', 'adapter', 'splitter', 'switch',
    'hub', 'extender', 'amplifier', 'connector', 'coupler'
  ],
  equipment: [
    'tv', 'television', 'monitor', 'display', 'screen', 'projector',
    'speaker', 'soundbar', 'audio', 'video', 'player', 'media'
  ]
};

// Category mappings to fallback images
const CATEGORY_FALLBACKS: { [key: string]: string } = {
  'cables': '/images/defaults/cables.svg',
  'cable': '/images/defaults/cables.svg',
  'connectivity': '/images/defaults/cables.svg',
  'set-top boxes': '/images/defaults/set-top-box.svg',
  'stb': '/images/defaults/set-top-box.svg',
  'receivers': '/images/defaults/set-top-box.svg',
  'remotes': '/images/defaults/remote.svg',
  'remote controls': '/images/defaults/remote.svg',
  'accessories': '/images/defaults/accessory.svg',
  'equipment': '/images/defaults/no-image.png',
  'electronics': '/images/defaults/no-image.png',
  'audio': '/images/defaults/no-image.png',
  'video': '/images/defaults/no-image.png'
};

/**
 * Analyzes product name for keywords to determine product type
 */
function analyzeProductName(productName: string): string | null {
  if (!productName) return null;
  
  const lowerName = productName.toLowerCase();
  
  // Check each keyword category
  for (const [type, keywords] of Object.entries(PRODUCT_KEYWORDS)) {
    if (keywords.some(keyword => lowerName.includes(keyword))) {
      switch (type) {
        case 'cables':
          return '/images/defaults/cables.svg';
        case 'setTopBox':
          return '/images/defaults/set-top-box.svg';
        case 'remote':
          return '/images/defaults/remote.svg';
        case 'accessory':
          return '/images/defaults/accessory.svg';
        case 'equipment':
          return '/images/defaults/no-image.png';
        default:
          return null;
      }
    }
  }
  
  return null;
}

/**
 * Analyzes SKU patterns to determine product type
 */
function analyzeSkuPattern(sku: string): string | null {
  if (!sku) return null;
  
  const upperSku = sku.toUpperCase();
  
  // Common SKU patterns
  if (upperSku.includes('CBL') || upperSku.includes('CABLE')) {
    return '/images/defaults/cables.svg';
  }
  if (upperSku.includes('STB') || upperSku.includes('RCV')) {
    return '/images/defaults/set-top-box.svg';
  }
  if (upperSku.includes('RMT') || upperSku.includes('RC')) {
    return '/images/defaults/remote.svg';
  }
  if (upperSku.includes('ACC') || upperSku.includes('MISC')) {
    return '/images/defaults/accessory.svg';
  }
  
  return null;
}

/**
 * Determines the appropriate fallback image for a product
 */
export function getProductFallbackImage(product: ProductInfo): string {
  // First, check category mapping
  if (product.category) {
    const categoryLower = product.category.toLowerCase();
    const categoryFallback = CATEGORY_FALLBACKS[categoryLower];
    if (categoryFallback) {
      return categoryFallback;
    }
    
    // Check if category contains any keywords
    for (const [key, value] of Object.entries(CATEGORY_FALLBACKS)) {
      if (categoryLower.includes(key)) {
        return value;
      }
    }
  }
  
  // Second, analyze product name
  const nameBasedFallback = analyzeProductName(product.name || '');
  if (nameBasedFallback) {
    return nameBasedFallback;
  }
  
  // Third, analyze SKU pattern
  const skuBasedFallback = analyzeSkuPattern(product.sku || '');
  if (skuBasedFallback) {
    return skuBasedFallback;
  }
  
  // Default fallback - use the "no image available" icon
  return '/images/defaults/no-image.png';
}

/**
 * Gets a category icon class based on product type
 */
export function getCategoryIconClass(product: ProductInfo): string {
  const fallbackImage = getProductFallbackImage(product);
  
  switch (fallbackImage) {
    case '/images/defaults/cables.svg':
      return 'fas fa-ethernet';
    case '/images/defaults/set-top-box.svg':
      return 'fas fa-tv';
    case '/images/defaults/remote.svg':
      return 'fas fa-gamepad';
    case '/images/defaults/accessory.svg':
      return 'fas fa-cog';
    default:
      return 'fas fa-box';
  }
}

/**
 * Checks if a product likely has an image based on its attributes
 */
export function productLikelyHasImage(product: ProductInfo): boolean {
  // Products with certain keywords are less likely to have images
  const noImageKeywords = ['generic', 'misc', 'other', 'spare', 'replacement'];
  
  if (product.name) {
    const lowerName = product.name.toLowerCase();
    return !noImageKeywords.some(keyword => lowerName.includes(keyword));
  }
  
  return true;
}